import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { getOrCreateDeviceId, getDeviceName } from '../utils/device'
import { handleSessionExpired, clearInvalidTokens } from '../utils/authHelpers'
import { createLogger } from '../utils/logger'

const logger = createLogger('ApiClient')

declare global {
  interface Window {
    __API_BASE_URL?: string
    __setGalateaApiBaseUrl?: (url: string | null) => void
  }
}

const DEV_PORTS = new Set(['3000', '5173', '5174', '5178', '4173', '7081', '9083', '9084'])

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean }

type PendingRequest = {
  resolve: (token: string) => void
  reject: (error: unknown) => void
}

let isRefreshing = false
let refreshPromise: Promise<string> | null = null
const pendingRequests: PendingRequest[] = []
function processQueue(error: unknown, token: string | null) {
  while (pendingRequests.length) {
    const { resolve, reject } = pendingRequests.shift()!
    if (token) {
      resolve(token)
    } else {
      reject(error)
    }
  }
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = sessionStorage.getItem('refresh_token')
  if (!refreshToken) {
    throw new Error('No refresh token')
  }

  const currentBaseUrl = getApiBaseUrl()
  // Aumentar timeout para 15s em produção (latência de rede pode ser maior)
  const response = await axios.post(
    `${currentBaseUrl}/auth/refresh`,
    { refresh_token: refreshToken },
    { timeout: 15000 }
  )

  const { access_token, refresh_token } = response.data
  sessionStorage.setItem('access_token', access_token)
  sessionStorage.setItem('refresh_token', refresh_token)
  localStorage.setItem('access_token', access_token)
  localStorage.setItem('refresh_token', refresh_token)

  return access_token
}

function normalizeUrl(url: string): string {
  return url.replace(/\/+$/, '')
}

function readManualOverride(): string | null {
  if (typeof window === 'undefined') {
    return null
  }

  const globalOverride = window.__API_BASE_URL
  if (typeof globalOverride === 'string' && globalOverride.trim().length > 0) {
    return normalizeUrl(globalOverride.trim())
  }

  try {
    const stored = localStorage.getItem('galatea_api_base_url')
    if (stored && stored.trim().length > 0) {
      return normalizeUrl(stored.trim())
    }
  } catch (error) {
    logger.warn('Não foi possível ler galatea_api_base_url do localStorage', error)
  }

  const envOverride = import.meta.env?.VITE_API_BASE_URL
  if (typeof envOverride === 'string' && envOverride.trim().length > 0) {
    return normalizeUrl(envOverride.trim())
  }

  return null
}

function computeBaseUrlCandidates(): string[] {
  const override = readManualOverride()
  if (override) {
    return [override]
  }

  const port = window.location.port || ''
  const isDevPort = DEV_PORTS.has(port)

  // Em desenvolvimento local, usar proxy do Vite (/api -> backend)
  // O Vite roteia /api/* para o backend (vite.config.ts); sem /api o login não chega ao backend
  if (isDevPort) {
    return ['/api']
  }

  // Em produção (Amazon), usar a origem atual + /api
  // Exemplo: https://app.galateaia.com -> https://app.galateaia.com/api
  const origin = window.location.origin
  // Se a URL já termina com /api, não adicionar novamente
  if (origin.endsWith('/api')) {
    return [origin]
  }
  return [`${origin}/api`]
}

function getApiBaseUrl(): string {
  const candidates = computeBaseUrlCandidates()
  if (candidates.length === 0) {
    return 'http://localhost:8000'
  }
  const first = candidates[0]
  // Em dev com proxy, '' não é usado; fallback explícito
  if (first === '' && typeof window !== 'undefined') {
    const port = window.location.port || ''
    if (DEV_PORTS.has(port)) return '/api'
  }
  return first
}

if (typeof window !== 'undefined') {
  window.__setGalateaApiBaseUrl = (url: string | null) => {
    try {
      if (url && url.trim().length > 0) {
        localStorage.setItem('galatea_api_base_url', normalizeUrl(url.trim()))
      } else {
        localStorage.removeItem('galatea_api_base_url')
      }
    } catch (error) {
      logger.warn('Não foi possível persistir galatea_api_base_url', error)
    }
  }
}


// Criar cliente sem baseURL fixo - será definido dinamicamente no interceptor
export const apiClient: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para adicionar token, device_id e definir baseURL
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Definir baseURL diretamente (sem sistema de candidatos)
    if (!config.baseURL) {
      config.baseURL = getApiBaseUrl()
    }

    // Logs apenas em desenvolvimento (reduzir spam em produção)
    if (import.meta.env.DEV) {
      const baseUrl = config.baseURL || getApiBaseUrl()
      const fullUrl = `${baseUrl}${config.url}`
      logger.debug('API Request:', config.method?.toUpperCase(), fullUrl)
    }

    const token = sessionStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Adicionar device_id e device_name em todas as requisições
    let deviceId = 'browser-device'
    let deviceName = 'Web Browser'
    try {
      deviceId = getOrCreateDeviceId()
    } catch (error) {
      logger.warn('Falha ao obter device_id, usando fallback', error)
    }
    try {
      deviceName = getDeviceName()
    } catch (error) {
      logger.warn('Falha ao obter device_name, usando fallback', error)
    }

    config.headers['X-Device-Id'] = deviceId
    config.headers['X-Device-Name'] = deviceName

    // USUARIO e ADMIN sempre atuam no tenant: injetar empresa_id em requisições que precisam
    const url = config.url || ''
    const precisaTenant = url.startsWith('/grid/') || url.includes('/telas/menu/hierarquico')
    if (precisaTenant && typeof window !== 'undefined') {
      try {
        const ctxStr = localStorage.getItem('user_context')
        const ctx = ctxStr ? JSON.parse(ctxStr) : null
        const empresaId = ctx?.empresa_id
        if (empresaId != null) {
          config.params = { ...config.params, empresa_id: empresaId }
        }
      } catch {
        // Ignorar erro ao ler contexto
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para refresh token automático
apiClient.interceptors.response.use(
  (response) => {
    // Logs apenas em desenvolvimento
    if (import.meta.env.DEV) {
      logger.debug('Response OK:', response.status, response.config.url)
    }
    return response
  },
  async (error) => {
    // Logs detalhados apenas em desenvolvimento ou para erros críticos
    if (import.meta.env.DEV || error.response?.status === 401) {
      const baseUrl = error.config?.baseURL || getApiBaseUrl()
      const fullUrl = error.config ? `${baseUrl}${error.config.url}` : 'URL desconhecida'

      logger.error('Response Error:', error.message)
      logger.error('URL completa:', fullUrl)
      logger.error('Status:', error.response?.status)

      if (!error.response) {
        logger.error('ERRO DE CONEXÃO - Backend não está respondendo')
        logger.error('Verifique se o backend está rodando em:', baseUrl)
      }
      if (error.response?.data?.detail && import.meta.env.DEV) {
        logger.error('Detail:', error.response.data.detail)
      }
    }

    const originalRequest = error.config as RetryableRequestConfig | undefined

    if (error.response?.status === 401 && originalRequest) {
      // Se for endpoint de validação ou login, não tentar refresh - apenas rejeitar
      // O AuthContext vai lidar com isso
      if (originalRequest.url?.includes('/auth/validate') || originalRequest.url?.includes('/auth/login')) {
        return Promise.reject(error)
      }

      // Se já está tentando fazer refresh e falhou, limpar tokens e redirecionar
      if (originalRequest.url?.includes('/auth/refresh')) {
        clearInvalidTokens()
        handleSessionExpired({
          message: 'Sua sessão expirou. Por favor, faça login novamente.',
          redirectToLogin: true,
          showNotification: true,
        })
        return Promise.reject(error)
      }

      // Se já tentou fazer retry uma vez, não tentar novamente (evitar loop)
      if (originalRequest._retry) {
        logger.warn('Token refresh já foi tentado, evitando loop')
        clearInvalidTokens()
        handleSessionExpired({
          message: 'Sua sessão expirou. Por favor, faça login novamente.',
          redirectToLogin: true,
          showNotification: true,
        })
        return Promise.reject(error)
      }

      originalRequest._retry = true

      return new Promise((resolve, reject) => {
        pendingRequests.push({
          resolve: (token: string) => {
            originalRequest.headers = originalRequest.headers || {}
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(originalRequest))
          },
          reject: (err) => {
            // Se o refresh falhar, limpar tokens e redirecionar
            clearInvalidTokens()
            handleSessionExpired({
              message: 'Sua sessão expirou. Por favor, faça login novamente.',
              redirectToLogin: true,
              showNotification: true,
            })
            reject(err)
          },
        })

        if (!isRefreshing) {
          isRefreshing = true
          logger.debug('Tentando refresh token...')

          refreshPromise = refreshAccessToken()
          refreshPromise
            .then((newToken) => {
              logger.debug('Token atualizado com sucesso')
              processQueue(null, newToken)
            })
            .catch((refreshError) => {
              logger.error('Erro ao fazer refresh token:', refreshError)
              clearInvalidTokens()
              handleSessionExpired({
                message: 'Sua sessão expirou. Por favor, faça login novamente.',
                redirectToLogin: true,
                showNotification: true,
              })
              processQueue(refreshError, null)
            })
            .finally(() => {
              isRefreshing = false
              refreshPromise = null
            })
        }
      })
    }

    // Se for erro 403 (sem permissão), não limpar tokens - apenas mostrar erro
    if (error.response?.status === 403) {
      // Não limpar tokens em caso de 403 - é problema de permissão, não de autenticação
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export default apiClient

export const resetApiClientState = (): void => {
  isRefreshing = false
  refreshPromise = null
  pendingRequests.length = 0
}

